<template>
  <div>
    <!-- ==================================================================
         INTERNAL CHROME — not part of what a practice sees. Kept on a gray
         band with an explicit label so the boundary is unmistakable during a
         demo: everything below the divider is the practice's view.
         ================================================================== -->
    <div class="bg-growth-10 border-b border-gray-200 px-5 py-4">
      <div class="flex items-end gap-4 flex-wrap">
        <div class="grow max-w-md">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Internal · beta preview
          </label>
          <select
            v-model="selectedPractice"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            @change="loadProviders"
          >
            <option :value="null" disabled>Select a practice to preview…</option>
            <option v-for="p in practices" :key="p.practice_entity" :value="p.practice_entity">
              <span class="sensitive">{{ p.practice_name }}</span> — {{ p.providers }} provider{{ p.providers === 1 ? '' : 's' }}<span v-if="p.tins > 1"> · {{ p.tins }} TINs</span><span v-if="p.pending"> · {{ p.pending }} pending</span>
            </option>
          </select>
        </div>
        <p class="text-xs text-gray-500 pb-2 max-w-sm">
          The practice never sees this selector. They arrive on a link scoped to
          their own practice only.
        </p>
      </div>
    </div>

    <div v-if="error" class="mx-5 mt-4 p-4 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
      {{ error }}
    </div>

    <div v-if="!selectedPractice" class="px-5 py-10 text-center text-sm text-gray-500">
      Select a practice above to see what they would be asked to confirm.
    </div>
    <LoadingIndicator v-else-if="loading" message="Loading providers…" />

    <!-- ==================================================================
         THE PRACTICE'S VIEW starts here.
         ================================================================== -->
    <template v-else>
      <PageHeader
        :title="practiceName"
        title-class="sensitive"
        :heading-level="2"
        subtitle="Confirm the providers who practice at your locations for Medicare ACO reporting"
      />

      <div class="p-5">
        <div class="max-w-3xl">
          <p class="text-sm text-gray-800">
            Please confirm the providers below practice at your locations. Reject
            anyone who does not, and add any we have missed. This roster determines
            who we report to Medicare on your behalf, so it matters that it is right.
          </p>

          <!-- Attestation: who is answering. No login, so this is the only -->
          <!-- record of who confirmed, which is why it sits with the content. -->
          <div class="mt-4 flex items-end gap-4 flex-wrap">
            <div class="max-w-xs grow">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Your name <span class="text-gray-500 font-normal">— so we know who confirmed</span>
              </label>
              <input
                v-model="reviewerName"
                type="text"
                placeholder="First and last name"
                class="sensitive w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
              />
            </div>
            <div class="flex items-center gap-2 pb-2 text-xs">
              <span class="text-gray-600">{{ providers.length }} provider{{ providers.length === 1 ? '' : 's' }}</span>
              <Tag v-if="counts.pending" :label="`${counts.pending} to review`" color="amber" />
              <Tag v-if="counts.approved" :label="`${counts.approved} confirmed`" color="emerald" />
              <Tag v-if="counts.rejected" :label="`${counts.rejected} rejected`" color="red" />
            </div>
          </div>

          <!-- Provider cards -->
          <div class="mt-5 space-y-4">
            <div
              v-for="row in providers"
              :key="row.id"
              class="bg-white rounded-lg border overflow-hidden"
              :class="row.status === 'rejected' ? 'border-gray-200' : 'border-growth-50'"
            >
              <!-- ---------- read-only ---------- -->
              <div v-if="!isNew(row)" class="p-4">
                <div class="flex items-start justify-between gap-4 flex-wrap">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2 mb-2 flex-wrap">
                      <Tag :label="statusLabel(row)" :color="statusColor(row)" />
                      <Tag v-if="row.source === 'practice_added'" label="Added by you" color="blue" />
                    </div>

                    <p
                      class="text-sm font-medium text-gray-900 sensitive"
                      :class="row.status === 'rejected' ? 'line-through text-gray-500' : ''"
                    >
                      {{ row.provider_name || '(unnamed provider)' }}
                    </p>
                    <p class="text-sm text-gray-600">
                      NPI <span class="font-mono sensitive">{{ row.npi || 'not on file' }}</span>
                    </p>

                    <p v-if="row.practice_comment" class="mt-2 text-xs text-gray-600">
                      <span class="sensitive" data-demo-blur>“{{ row.practice_comment }}”</span><span v-if="row.practice_reviewer_name" class="sensitive text-gray-500"> — {{ row.practice_reviewer_name }}</span>
                    </p>
                  </div>

                  <div class="flex items-center gap-2 shrink-0">
                    <button
                      class="btn-primary-sm"
                      :disabled="busyId === row.id"
                      @click="decide(row, 'approved')"
                    >
                      Confirm
                    </button>
                    <button
                      class="btn-secondary-sm"
                      :disabled="busyId === row.id"
                      @click="startReject(row)"
                    >
                      Reject
                    </button>
                  </div>
                </div>

                <!-- reject reason -->
                <div v-if="rejectingId === row.id" class="mt-4 pt-4 border-t border-gray-200">
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Why does this provider not belong on your roster?
                    <span class="text-gray-500 font-normal">— optional</span>
                  </label>
                  <div class="flex items-center gap-2 flex-wrap">
                    <input
                      v-model="rejectComment"
                      type="text"
                      placeholder="e.g. left the practice in 2024"
                      class="grow max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                      @keyup.enter="decide(row, 'rejected')"
                    />
                    <button class="btn-danger-sm" @click="decide(row, 'rejected')">
                      Reject provider
                    </button>
                    <button class="btn-link text-xs" @click="rejectingId = null">Cancel</button>
                  </div>
                </div>
              </div>

              <!-- ---------- add ---------- -->
              <div v-else class="p-4 bg-growth-10">
                <div class="grid grid-cols-6 gap-4">
                  <div class="col-span-6 sm:col-span-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Provider name</label>
                    <input v-model="draft.provider_name" type="text" placeholder="First and last name" class="sensitive" :class="inputClass" />
                  </div>
                  <div class="col-span-6 sm:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      NPI <span class="text-gray-500 font-normal">— optional</span>
                    </label>
                    <input v-model="draft.npi" type="text" maxlength="20" class="sensitive" :class="inputClass" />
                  </div>
                </div>

                <div class="flex items-center gap-2 mt-5 flex-wrap">
                  <button class="btn-primary-sm" :disabled="busyId === row.id" @click="saveNew(row)">
                    {{ busyId === row.id ? 'Saving…' : 'Add provider' }}
                  </button>
                  <button class="btn-link text-xs" @click="cancelAdd">Cancel</button>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="!addingNew"
            class="w-full mt-4 py-4 text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-800 transition-colors"
            @click="startAdd"
          >
            + Add a provider we missed
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'

const BASE = '/api/rostering/providers/'

const STATUS_LABELS = {
  pending: 'Needs review',
  approved: 'Confirmed',
  rejected: 'Rejected',
}
const STATUS_COLORS = {
  pending: 'amber',
  approved: 'emerald',
  rejected: 'red',
}

export default {
  name: 'PracticeProviderView',
  components: { PageHeader, LoadingIndicator, Tag },
  props: {
    // Set when arriving from a report row, so the picker lands pre-selected.
    initialPractice: { type: [Number, String], default: null },
  },
  data() {
    return {
      practices: [],
      selectedPractice: null,
      reviewerName: '',
      providers: [],
      loading: false,
      error: null,
      rejectingId: null,
      rejectComment: '',
      busyId: null,
      addingNew: false,
      draft: {},
      inputClass:
        'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent',
    }
  },
  computed: {
    practiceName() {
      const p = this.practices.find(x => x.practice_entity === this.selectedPractice)
      return p ? p.practice_name : ''
    },
    counts() {
      const tally = { pending: 0, approved: 0, rejected: 0 }
      for (const r of this.providers) {
        if (tally[r.status] !== undefined) tally[r.status]++
      }
      return tally
    },
  },
  watch: {
    initialPractice(id) {
      if (id) this.selectPractice(id)
    },
  },
  mounted() {
    this.loadPractices()
  },
  methods: {
    async loadPractices() {
      try {
        const { data } = await axios.get(`${BASE}opportunity-practices/`)
        this.practices = data
        if (this.initialPractice) this.selectPractice(this.initialPractice)
      } catch (e) {
        this.error = this.errText(e, 'Could not load practices')
      }
    },
    selectPractice(id) {
      this.selectedPractice = Number(id)
      this.loadProviders()
    },
    async loadProviders() {
      if (!this.selectedPractice) return
      this.loading = true
      this.error = null
      this.resetForms()
      try {
        const { data } = await axios.get(`${BASE}opportunity-report/`, {
          params: { practice_entity: this.selectedPractice },
        })
        this.providers = data.results || data
      } catch (e) {
        this.error = this.errText(e, 'Could not load providers')
      } finally {
        this.loading = false
      }
    },

    // ---- review -----------------------------------------------------
    async decide(row, decision) {
      this.busyId = row.id
      this.error = null
      try {
        const { data } = await axios.post(`${BASE}opportunity-decide/`, {
          work_id: row.id,
          decision,
          side: 'practice',
          reviewer_name: this.reviewerName,
          comment: decision === 'rejected' ? this.rejectComment : '',
        })
        this.replaceRow(row.id, data)
        this.rejectingId = null
        this.rejectComment = ''
      } catch (e) {
        this.error = this.errText(e, 'Could not save that')
      } finally {
        this.busyId = null
      }
    },
    startReject(row) {
      this.rejectingId = row.id
      this.rejectComment = ''
    },

    // ---- add --------------------------------------------------------
    startAdd() {
      // A new provider still needs a TIN, which this page deliberately never
      // shows. Anchor it to a TIN the practice already has; internal staff can
      // reassign it later.
      const anchor = this.providers[0]
      if (!anchor) {
        this.error = 'Nothing to anchor a new provider to yet for this practice.'
        return
      }
      const placeholder = {
        id: '__new__',
        tin_entity: anchor.tin_entity,
        practice_entity: this.selectedPractice,
        status: 'pending',
        source: 'practice_added',
        provider_name: '',
        npi: '',
      }
      this.providers.push(placeholder)
      this.addingNew = true
      this.draft = { provider_name: '', npi: '' }
    },
    isNew(row) {
      return row.id === '__new__'
    },

    async saveNew(row) {
      this.busyId = row.id
      this.error = null
      try {
        const { data } = await axios.post(`${BASE}opportunity-add/`, {
          tin_entity: row.tin_entity,
          practice_entity: this.selectedPractice,
          side: 'practice',
          reviewer_name: this.reviewerName,
          ...this.draft,
        })
        // Replace by identity, not by position — the placeholder is appended today
        // but relying on "it is last" breaks the moment anything else reorders it.
        const n = this.providers.findIndex(r => this.isNew(r))
        if (n !== -1) this.providers.splice(n, 1, data)
        this.addingNew = false
        this.draft = {}
      } catch (e) {
        this.error = this.errText(e, 'Could not add the provider')
      } finally {
        this.busyId = null
      }
    },
    cancelAdd() {
      const i = this.providers.findIndex(r => this.isNew(r))
      if (i !== -1) this.providers.splice(i, 1)
      this.addingNew = false
      this.draft = {}
    },

    // ---- helpers ----------------------------------------------------
    replaceRow(id, data) {
      const i = this.providers.findIndex(r => r.id === id)
      if (i !== -1) this.providers.splice(i, 1, data)
    },
    resetForms() {
      this.rejectingId = null
      this.addingNew = false
      this.draft = {}
    },
    statusLabel(row) {
      return STATUS_LABELS[row.status] || row.status
    },
    statusColor(row) {
      return STATUS_COLORS[row.status] || 'gray'
    },
    errText(e, fallback) {
      return e.response?.data?.detail || e.response?.data?.error || fallback
    },
  },
}
</script>
